/**
 * UrlCaptureDialog — paste a landing-page URL or a direct video file URL.
 * Pages are captured server-side as a full-page screenshot. Direct video FILE
 * urls skip capture (the file IS the asset). YouTube / Vimeo / Drive / Dropbox
 * links are refused: twins need keyframes we cannot extract from those hosts.
 *
 * The video classification happens HERE, client-side, before `captureUrl` is
 * called: the capture endpoint never sets `source`, so a caller reading
 * `result.source` only ever sees what this dialog put there.
 */

import { useState } from 'react'
import { Loader2 } from '@digital-twins/design-system/icons'
import { useTranslation } from 'react-i18next'

import {
  isDirectVideoUrl,
  linksOnly,
  nonKeyframeableVideoMessage,
} from '@digital-twins/shared-types'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { UrlCaptureResult } from '@/lib/api'
import {
  friendlyCaptureError,
  refusedCaptureError,
  type CaptureUrl,
} from '@/components/media/useUrlCaptures'

interface UrlCaptureDialogProps {
  /** The capture on the caller's own HTTP client. */
  capture: CaptureUrl
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The accepted capture, with the link it was taken from, normalized. */
  onAccept: (result: UrlCaptureResult, url: string) => void
  /**
   * A page whose capture failed is still its link (`savesCaptureOfLink`, the
   * collect card's rule): the Save captures it, and readiness says what it
   * needs. Given for a family that rule names; any other family's link shows
   * nothing without a capture, so its failure stays this dialog's error.
   */
  onAcceptLink?: (url: string, line: string) => void
  /** Retina factor for the capture (chat passes 1 to keep the image small). */
  scale?: number
  title?: string
  /**
   * The server's verdict that this asset's link shows another page, a block
   * or a blank (its readiness `page_capture_*` line, `NOT_THE_PAGE_CAPTURE_CODES`):
   * the Save drops any picture of that link, so the dialog offers none of it
   * and says the line (H08-LP).
   */
  notThePage?: { url: string; line: string } | null
}

/** Two links to one address, as the collect write compares them (letter case aside). */
function sameLink(a: string, b: string): boolean {
  const [first] = linksOnly(a)
  return Boolean(first) && first.toLowerCase() === b.toLowerCase()
}

export function UrlCaptureDialog({
  capture,
  open,
  onOpenChange,
  onAccept,
  onAcceptLink,
  scale,
  title = 'Add asset',
  notThePage = null,
}: UrlCaptureDialogProps) {
  const { t } = useTranslation('app')
  const [url, setUrl] = useState('')
  const [fetching, setFetching] = useState(false)
  const [result, setResult] = useState<UrlCaptureResult | null>(null)
  // The normalized link the shown capture was taken of (E-session-14).
  const [capturedLink, setCapturedLink] = useState('')
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setUrl('')
    setFetching(false)
    setResult(null)
    setCapturedLink('')
    setError(null)
  }
  const close = () => {
    onOpenChange(false)
    setTimeout(reset, 200)
  }

  const fetchCapture = async () => {
    const u = url.trim()
    if (!u) return
    setFetching(true)
    setError(null)
    setResult(null)
    try {
      // FIRST, before anything is fetched. Screenshotting one of these produces
      // a still of a player page that looks like a successful capture and is
      // useless as a stimulus.
      const blocked = nonKeyframeableVideoMessage(u)
      if (blocked) {
        setError(blocked)
        return
      }
      // One link, by the one URL format the chat and the collect card read
      // (`linksOnly`): absolute, a bare host taking https. Anything else is
      // capture's `invalid_url`, said before a capture is spent on it.
      const links = linksOnly(u)
      if (links.length !== 1) {
        setError(friendlyCaptureError('invalid_url', null))
        return
      }
      const normalized = links[0]
      if (notThePage && sameLink(notThePage.url, normalized)) {
        setError(notThePage.line)
        return
      }
      // A direct video file is the asset itself; there is no page to capture.
      if (isDirectVideoUrl(u)) {
        setCapturedLink(normalized)
        setResult({
          ok: true,
          publicUrl: normalized,
          pageTitle: 'Video',
          source: 'video',
        })
        return
      }
      const res = await capture(u, { scale, fullPage: true })
      if (res.ok && (res.publicUrl || res.screenshotBase64)) {
        setCapturedLink(normalized)
        setResult(res)
      } else if (onAcceptLink) {
        onAcceptLink(normalized, friendlyCaptureError(res.failureReason, res.detail))
      } else {
        setError(refusedCaptureError(res.failureReason, res.detail))
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Capture failed.')
    } finally {
      setFetching(false)
    }
  }

  const previewSrc = result
    ? result.publicUrl ||
      (result.screenshotBase64 ? `data:image/png;base64,${result.screenshotBase64}` : '')
    : ''
  const videoPreviewUrl =
    result?.source === 'video' ? result.publicUrl || '' : ''

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {t('questionnaire.urlCapture.description')}
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="flex flex-col gap-3" data-testid="url-capture-fetch">
            <div className="flex gap-2">
              <Input
                type="url"
                inputMode="url"
                placeholder={t('questionnaire.urlCapture.placeholder')}
                value={url}
                disabled={fetching}
                autoFocus
                data-testid="url-capture-input"
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    void fetchCapture()
                  }
                }}
              />
              <Button
                type="button"
                onClick={() => void fetchCapture()}
                disabled={fetching || !url.trim()}
                data-testid="url-capture-fetch-btn"
              >
                {fetching ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    Capturing…
                  </>
                ) : (
                  'Fetch'
                )}
              </Button>
            </div>
            {fetching && (
              <p className="text-sm text-muted-foreground">
                Capturing the full page — this can take ~20-30s.
              </p>
            )}
            {error && (
              <p className="text-sm text-[#E5484D]" data-testid="url-capture-error">
                {error}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3" data-testid="url-capture-preview">
            {videoPreviewUrl ? (
              <div className="overflow-hidden rounded-md border border-border bg-background">
                <video
                  src={videoPreviewUrl}
                  controls
                  className="aspect-video w-full"
                  data-testid="url-capture-preview-video"
                />
              </div>
            ) : (
              <div className="max-h-[55vh] overflow-y-scroll rounded-md border border-border bg-background [&::-webkit-scrollbar]:w-2.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-foreground/30 hover:[&::-webkit-scrollbar-thumb]:bg-foreground/50">
                <img
                  src={previewSrc}
                  alt={result.pageTitle || 'Captured page'}
                  className="w-full"
                  data-testid="url-capture-preview-img"
                />
              </div>
            )}
            <p className="truncate text-xs text-muted-foreground">
              {videoPreviewUrl
                ? `${result.pageTitle || 'Video'} · twins will watch the video`
                : `${result.pageTitle || url} · scroll to see the full page`}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setResult(null)
                  setError(null)
                }}
              >
                {videoPreviewUrl ? 'Change URL' : 'Recapture'}
              </Button>
              <Button type="button" variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button
                type="button"
                data-testid="url-capture-accept"
                onClick={() => {
                  onAccept(result, capturedLink)
                  close()
                }}
              >
                {videoPreviewUrl ? 'Use this video' : 'Use this image'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
